import { Fragment } from 'react'
import { AssetReference } from '@opallabs/cassini'
import { Asset } from '@opallabs/kuiper'
import { DragAndDropAssetGallery } from '../../../../../DragAndDropAssetGallery'
import { twitterTweetPositions } from '../../../../../DragAndDropAssetGallery/positions'
import { LinkPreviewAssetCarousel } from '../../../../../LinkPreviewAssetCarousel'
import {
  twitterTweetDesktopFields,
  twitterTweetMobileFields,
} from '../../../../../LinkPreviewAssetCarousel/fields'
import { useContentWithLinkPreviewAssetCarousel } from '../../../../helpers/hooks'
import {
  LinkWithData,
  OnLinkDataUpdate,
  OnShowAssetPickerProps,
} from '../../../../helpers/types'
import { TwitterSingleFooter } from '../../../sharedDisplays/footers/TwitterSingleFooter'
import { TwitterSingleHeader } from '../../../sharedDisplays/headers/TwitterSingleHeader'
import { styles } from './styles'

export interface Props {
  accountImage: AssetReference | null
  accountName: string
  accountUsername: string
  assets: AssetReference[]
  content: string
  display: string
  isAccountPremium?: boolean
  isPresenting: boolean
  isSnapshot: boolean
  linksWithData: LinkWithData[]
  onAssetChange: () => void
  onContentChange: (val: string) => void
  onExpandAsset: (assetId: number) => void
  onLinkDataUpdate: OnLinkDataUpdate
  onRemoveAsset: (assetId: number) => void
  onShowAssetPicker: (args: OnShowAssetPickerProps) => void
  readOnly: boolean
  scheduledAt: string
  timezone: string
  updateCaption: () => void
}

export const Single: React.FC<Props> = ({
  accountImage,
  accountName,
  accountUsername,
  assets,
  content,
  display = 'mobileSingle',
  isAccountPremium,
  isPresenting = false,
  linksWithData,
  onAssetChange,
  onContentChange,
  onExpandAsset,
  onLinkDataUpdate,
  onRemoveAsset,
  onShowAssetPicker,
  readOnly,
  scheduledAt,
  timezone,
  updateCaption,
}) => {
  const isMobile = display.includes('mobile')
  const { link, isPersistingLink, setIsPersistingLink, linkWithData } =
    useContentWithLinkPreviewAssetCarousel({
      content,
      linksWithData,
    })
  const linkAssets = linkWithData ? linkWithData.assetReferences : []

  return (
    <Fragment>
      <TwitterSingleHeader
        accountImage={accountImage}
        accountName={accountName}
        accountUsername={accountUsername}
        content={content}
        isAccountPremium={isAccountPremium}
        isMobile={isMobile}
        onContentChange={onContentChange}
        readOnly={readOnly}
      />
      {assets.length === 0 && isPersistingLink && (
        <div css={styles.contentWrapper}>
          <LinkPreviewAssetCarousel
            assetStyle={styles.getLinkAssetStyle(
              linkAssets.length > 0,
              display
            )}
            disableRemoveLink={false}
            fields={
              isMobile ? twitterTweetMobileFields : twitterTweetDesktopFields
            }
            descriptionStyle={styles.getLinkDescriptionStyle(isMobile)}
            domainStyle={styles.getLinkDomainStyle(isMobile)}
            hasRemovedLink={() => setIsPersistingLink(false)}
            hasSquareInlineAsset={false}
            link={link}
            linkStyle={styles.getLinkStyle(linkAssets.length > 0, display)}
            linkWithData={linkWithData}
            onLinkDataUpdate={onLinkDataUpdate}
            onShowAssetPicker={onShowAssetPicker}
            readOnly={readOnly || isPresenting}
            titleStyle={styles.linkTitleStyle}
          />
        </div>
      )}
      {assets.length === 1 && (
        <div css={styles.contentWrapper}>
          <Asset
            assetReference={assets[0]}
            hasAssetOptions={true}
            hasAssetOverlay={assets[0] && (!readOnly || isPresenting)}
            imageFit="cover"
            onEmbiggen={(): void => onExpandAsset(assets[0].id || 0)}
            onRemove={(): void => onRemoveAsset(assets[0].id || 0)}
            overrideStyles={styles.getAssetStyles(assets[0], isMobile)}
            placeholder={true}
            removeable={!isPresenting}
            updateCaption={updateCaption}
          />
        </div>
      )}
      {assets.length > 1 && (
        <div css={styles.contentWrapper}>
          <DragAndDropAssetGallery
            assets={assets}
            display={display}
            galleryContainerStyles={styles.getGalleryContainer(display)}
            hasAssetOptions={true}
            isPresenting={isPresenting}
            maxAssets={4}
            maxHeight={isMobile ? 193 : 320}
            maxWidth={isMobile ? 343 : 568}
            onAssetChange={onAssetChange}
            onExpandAsset={onExpandAsset}
            onRemoveAsset={onRemoveAsset}
            positions={twitterTweetPositions}
            readOnly={readOnly}
            updateCaption={updateCaption}
          />
        </div>
      )}
      <TwitterSingleFooter
        isMobile={isMobile}
        scheduledAt={scheduledAt}
        timezone={timezone}
      />
    </Fragment>
  )
}
